// src/services/dashboardAPI.ts
// Phase 1 Cleanup: Order status fields removed - Dashboard is now read-only
// Ordering workflow moved to Products page → Purchases

import { authService } from './authService';
import { inventoryAPI } from './inventoryAPI';
import { productCatalogAPI, Product } from './productCatalogAPI';
import { purchaseLineItemsAPI, ProductOnOrderSummary } from './purchaseLineItemsAPI';
import { logger } from '../utils/logger';
import { getApiBaseUrl } from './configService';
const dashboardLogger = logger.createServiceLogger('DashboardAPI');

// Dashboard Alert Types
export interface DashboardAlertCounts {
  expiredArmor: number;
  expiringSoon: number;
  criticalStock: number;
  lowStock: number;
  overdueOnboarding: number;
  readyToComplete: number;
  totalIncompleteOnboarding: number;
}

export interface BodyArmorAlert {
  itemId: string;
  itemName: string;
  serialNumber: string;
  expirationDate: string;
  daysUntilExpiration: number;
  assignedToName?: string;
  status: string;
  ballisticLevel: string;
}

export interface BodyArmorAlerts {
  expired: BodyArmorAlert[];
  expiringSoon: BodyArmorAlert[];
  nextExpirationDate?: string;
  nextExpirationDays?: number;
  totalBodyArmor: number;
}

// Simplified LowStockAlert - no order status fields
export interface LowStockAlert {
  itemId: string;
  itemName: string;
  availableQuantity: number;
  minimumStockLevel: number;
  totalQuantity: number;
  category: string;
}

export interface LowStockAlerts {
  criticalStock: LowStockAlert[];
  lowStock: LowStockAlert[];
  totalBulkItems: number;
}

export interface OnboardingAlert {
  sessionId: string;
  officerId: string;
  officerName: string;
  badgeNumber: string;
  status: string;
  daysOld: number;
  reservedItems: number;
  roleTemplateName: string;
}

export interface OnboardingAlerts {
  overdueSessions: OnboardingAlert[];
  recentSessions: OnboardingAlert[];
  readyToComplete: OnboardingAlert[];
  totalIncomplete: number;
}

// New: Dashboard product-level low stock (from Product Catalog)
export interface ProductLowStockAlert {
  productId: string;
  productName: string;
  categoryId: string;
  currentStock: number;
  minStockLevel: number;
  suggestedOrderQty: number;
}

export interface ProductLowStockAlerts {
  items: ProductLowStockAlert[];
  totalCount: number;
}

// New: Dashboard on-order tracking
export interface DashboardOnOrderItem {
  productId: string;
  productName: string;
  categoryId: string;
  totalPendingQuantity: number;
  orders: Array<{
    purchaseId: string;
    vendor?: string;
    purchaseDate?: string;
    poNumber?: string;
    quantity: number;
    allocatedQuantity: number;
    pendingQuantity: number;
  }>;
}

export interface OnOrderAlerts {
  items: DashboardOnOrderItem[];
  totalCount: number;
  totalPendingQuantity: number;
}

export interface DashboardData {
  success: boolean;
  tenantId: string;
  processingTime: number;
  alertCounts: DashboardAlertCounts;
  criticalAlerts: number;
  warningAlerts: number;
  lastUpdated: string;
  bodyArmorAlerts: BodyArmorAlerts;
  lowStockAlerts: LowStockAlerts;
  onboardingAlerts: OnboardingAlerts;
  // New Phase 4 fields
  productLowStockAlerts: ProductLowStockAlerts;
  onOrderAlerts: OnOrderAlerts;
  error?: string;
}

class DashboardAPI {
  private baseURL: string;

  constructor() {
    this.baseURL = getApiBaseUrl();
  }

  /**
   * Fetches dashboard data from DynamoDB via APIs
   * Dashboard is read-only - no order status management
   */
  async getDashboardData(): Promise<DashboardData> {
    const timer = logger.startTimer('DashboardAPI', 'getDashboardData');
    const startTime = Date.now();
    
    const tokenInfo = authService.getLastTokenInfo();
    const tenantId = tokenInfo?.tenantId || 'WallaWallaPD';

    dashboardLogger.info('Starting dashboard data fetch', {
      tenantId,
      timestamp: new Date().toISOString()
    });

    try {
      dashboardLogger.debug('Fetching real data from DynamoDB via parallel API calls');
      
      const [
        inventoryResult,
        assignmentsResult,
        onboardingResult,
        productsResult,
        onOrderResult
      ] = await Promise.allSettled([
        this.getInventoryData(),
        this.getAssignmentsData(),
        this.getOnboardingData(),
        this.getProductsData(),
        this.getOnOrderData()
      ]);

      const inventoryData = inventoryResult.status === 'fulfilled' ? inventoryResult.value : [];
      dashboardLogger.info('Inventory data loaded from DynamoDB', {
        itemCount: inventoryData.length,
        status: inventoryResult.status
      });

      const assignmentsData = assignmentsResult.status === 'fulfilled' ? assignmentsResult.value : [];
      dashboardLogger.info('Assignments data loaded from DynamoDB', {
        assignmentCount: assignmentsData.length,
        status: assignmentsResult.status
      });

      const onboardingData = onboardingResult.status === 'fulfilled' ? onboardingResult.value : [];
      dashboardLogger.info('Onboarding data loaded from DynamoDB', {
        sessionCount: onboardingData.length,
        status: onboardingResult.status
      });

      const productsData = productsResult.status === 'fulfilled' ? productsResult.value : [];
      dashboardLogger.info('Products data loaded from DynamoDB', {
        productCount: productsData.length,
        status: productsResult.status
      });

      const onOrderData = onOrderResult.status === 'fulfilled' ? onOrderResult.value : { data: [], summary: { totalProducts: 0, totalPendingItems: 0, totalPendingQuantity: 0 } };
      dashboardLogger.info('On-order data loaded from DynamoDB', {
        productsOnOrder: onOrderData.data.length,
        status: onOrderResult.status
      });

      // Calculate alerts from real DynamoDB data
      const bodyArmorAlerts = this.calculateBodyArmorAlerts(inventoryData, assignmentsData);
      const lowStockAlerts = this.calculateLowStockAlerts(inventoryData);
      const onboardingAlerts = this.calculateOnboardingAlerts(onboardingData);
      
      // New Phase 4: Calculate product-level alerts
      const onOrderAlerts = this.calculateOnOrderAlerts(onOrderData, productsData);
      const productLowStockAlerts = this.calculateProductLowStockAlerts(productsData, inventoryData, onOrderData);

      const alertCounts: DashboardAlertCounts = {
        expiredArmor: bodyArmorAlerts.expired.length,
        expiringSoon: bodyArmorAlerts.expiringSoon.length,
        criticalStock: lowStockAlerts.criticalStock.length,
        lowStock: lowStockAlerts.lowStock.length,
        overdueOnboarding: onboardingAlerts.overdueSessions.length,
        readyToComplete: onboardingAlerts.readyToComplete.length,
        totalIncompleteOnboarding: onboardingAlerts.totalIncomplete
      };

      const criticalAlerts = alertCounts.expiredArmor + alertCounts.criticalStock + alertCounts.overdueOnboarding;
      const warningAlerts = alertCounts.expiringSoon + alertCounts.lowStock + alertCounts.readyToComplete;

      const processingTime = Date.now() - startTime;
      const now = new Date().toISOString();

      dashboardLogger.info('Dashboard processing completed successfully', {
        processingTime,
        criticalAlerts,
        warningAlerts,
        tenantId,
        alertCounts
      });

      timer();

     return {
        success: true,
        tenantId: tenantId,
        processingTime: processingTime,
        alertCounts: alertCounts,
        criticalAlerts: criticalAlerts,
        warningAlerts: warningAlerts,
        lastUpdated: now,
        bodyArmorAlerts: bodyArmorAlerts,
        lowStockAlerts: lowStockAlerts,
        onboardingAlerts: onboardingAlerts,
        productLowStockAlerts: productLowStockAlerts,
        onOrderAlerts: onOrderAlerts
      };

    } catch (error: any) {
      const processingTime = Date.now() - startTime;
      const now = new Date().toISOString();

      timer();

      dashboardLogger.error('Failed to fetch dashboard data from DynamoDB', {
        error: error.message,
        processingTime,
        tenantId
      });
      
      return {
        success: false,
        error: `Failed to load dashboard data: ${error instanceof Error ? error.message : 'Unknown error'}`,
        tenantId: tenantId,
        processingTime: processingTime,
        alertCounts: {
          expiredArmor: 0,
          expiringSoon: 0,
          criticalStock: 0,
          lowStock: 0,
          overdueOnboarding: 0,
          readyToComplete: 0,
          totalIncompleteOnboarding: 0
        },
        criticalAlerts: 0,
        warningAlerts: 0,
        lastUpdated: now,
        bodyArmorAlerts: { 
          expired: [], 
          expiringSoon: [], 
          totalBodyArmor: 0 
        },
        lowStockAlerts: { 
          criticalStock: [], 
          lowStock: [], 
          totalBulkItems: 0 
        },
        onboardingAlerts: { 
          overdueSessions: [], 
          recentSessions: [], 
          readyToComplete: [], 
          totalIncomplete: 0 
        },
        productLowStockAlerts: {
          items: [],
          totalCount: 0
        },
        onOrderAlerts: {
          items: [],
          totalCount: 0,
          totalPendingQuantity: 0
        }
      };
    }
  }

  /**
   * Fetches inventory data from DynamoDB via inventory API
   */
  private async getInventoryData(): Promise<any[]> {
    const timer = logger.startTimer('DashboardAPI', 'getInventoryData');
    
    try {
      dashboardLogger.debug('Fetching inventory data from DynamoDB via inventory API');
      
      const response = await inventoryAPI.getItems();
      if (response.success && response.data) {
        dashboardLogger.debug('Inventory API call successful', {
          itemCount: response.data.length
        });
        
        timer();
        return response.data;
      }
      
      dashboardLogger.warn('Inventory API returned no data from DynamoDB', {
        response
      });
      
      timer();
      return [];
    } catch (error: any) {
      timer();
      
      dashboardLogger.error('Failed to fetch inventory data from DynamoDB', {
        error: error.message
      });
      throw error;
    }
  }

  /**
   * Fetches assignment data from DynamoDB via assignments API
   */
  private async getAssignmentsData(): Promise<any[]> {
    const timer = logger.startTimer('DashboardAPI', 'getAssignmentsData');
    
    try {
      dashboardLogger.debug('Fetching assignments data from DynamoDB via assignments API');
      
      const response = await authService.makeAuthenticatedRequest(`${this.baseURL}/assignments`, {
        method: 'GET'
      });

      if (response.ok) {
        const data = await response.json();
        const assignments = data.assignments || [];
        
        dashboardLogger.debug('Assignments API call successful', {
          assignmentCount: assignments.length
        });
        
        timer();
        return assignments;
      }
      
      dashboardLogger.warn('Assignments API returned no data from DynamoDB', {
        status: response.status,
        statusText: response.statusText
      });
      
      timer();
      return [];
    } catch (error: any) {
      timer();
      
      dashboardLogger.error('Failed to fetch assignments data from DynamoDB', {
        error: error.message
      });
      throw error;
    }
  }

  /**
   * Fetches onboarding session data from DynamoDB via onboarding API
   */
  private async getOnboardingData(): Promise<any[]> {
    const timer = logger.startTimer('DashboardAPI', 'getOnboardingData');
    
    try {
      dashboardLogger.debug('Fetching onboarding data from DynamoDB via onboarding API');
      
      const response = await authService.makeAuthenticatedRequest(`${this.baseURL}/onboarding/sessions`, {
        method: 'GET'
      });

      if (response.ok) {
        const data = await response.json();
        const sessions = data.sessions || [];
        
        dashboardLogger.debug('Onboarding API call successful', {
          sessionCount: sessions.length
        });
        
        timer();
        return sessions;
      }
      
      dashboardLogger.warn('Onboarding API returned no data from DynamoDB', {
        status: response.status,
        statusText: response.statusText
      });
      
      timer();
      return [];
    } catch (error: any) {
      timer();
      
      dashboardLogger.error('Failed to fetch onboarding data from DynamoDB', {
        error: error.message
      });
      throw error;
    }
  }

 /**
   * Fetches products data from Product Catalog API
   */
  private async getProductsData(): Promise<Product[]> {
    const timer = logger.startTimer('DashboardAPI', 'getProductsData');
    
    try {
      dashboardLogger.debug('Fetching products data from Product Catalog API');
      
      const products = await productCatalogAPI.listProducts({ active: true });
      
      dashboardLogger.debug('Products API call successful', {
        productCount: products.length
      });
      
      timer();
      return products;
    } catch (error: any) {
      timer();
      
      dashboardLogger.error('Failed to fetch products data', {
        error: error.message
      });
      return [];
    }
  }

  /**
   * Fetches on-order data from Purchase Line Items API
   */
  private async getOnOrderData(): Promise<{ data: ProductOnOrderSummary[]; summary: { totalProducts: number; totalPendingItems: number; totalPendingQuantity: number } }> {
    const timer = logger.startTimer('DashboardAPI', 'getOnOrderData');
    
    try {
      dashboardLogger.debug('Fetching on-order data from Purchase Line Items API');
      
      const response = await purchaseLineItemsAPI.getOpenLineItemsByProduct();
      
      dashboardLogger.debug('On-order API call successful', {
        productsOnOrder: response.data.length,
        totalPendingQuantity: response.summary.totalPendingQuantity
      });
      
      timer();
      return response;
    } catch (error: any) {
      timer();
      
      dashboardLogger.error('Failed to fetch on-order data', {
        error: error.message
      });
      return { data: [], summary: { totalProducts: 0, totalPendingItems: 0, totalPendingQuantity: 0 } };
    }
  }

  /**
   * Calculate on-order alerts from purchase line items
   */
  private calculateOnOrderAlerts(
    onOrderData: { data: ProductOnOrderSummary[]; summary: any },
    productsData: Product[]
  ): OnOrderAlerts {
    const timer = logger.startTimer('DashboardAPI', 'calculateOnOrderAlerts');
    
    const productMap = new Map(productsData.map(p => [p.productId, p]));
    
    const items: DashboardOnOrderItem[] = onOrderData.data
      .filter(item => item.productId) // Only items linked to products
      .map(item => {
        const product = productMap.get(item.productId!);
        return {
          productId: item.productId!,
          productName: product?.name || item.orders[0]?.itemDescription || 'Unknown Product',
          categoryId: product?.categoryId || '',
          totalPendingQuantity: item.totalPendingQuantity,
          orders: item.orders.map(o => ({
            purchaseId: o.purchaseId,
            vendor: o.vendor,
            purchaseDate: o.purchaseDate,
            poNumber: o.poNumber,
            quantity: o.quantity,
            allocatedQuantity: o.allocatedQuantity,
            pendingQuantity: o.pendingQuantity
          }))
        };
      });
    
    dashboardLogger.info('On-order alerts calculated', {
      totalProducts: items.length,
      totalPendingQuantity: onOrderData.summary.totalPendingQuantity
    });
    
    timer();
    
    return {
      items,
      totalCount: items.length,
      totalPendingQuantity: onOrderData.summary.totalPendingQuantity
    };
  }

  /**
   * Calculate product-level low stock alerts (excludes items on order)
   */
  private calculateProductLowStockAlerts(
    productsData: Product[],
    inventoryData: any[],
    onOrderData: { data: ProductOnOrderSummary[]; summary: any }
  ): ProductLowStockAlerts {
    const timer = logger.startTimer('DashboardAPI', 'calculateProductLowStockAlerts');
    
    // Build set of productIds that are on order
    const productsOnOrder = new Set(
      onOrderData.data
        .filter(item => item.productId)
        .map(item => item.productId!)
    );
    
    // Build inventory stock map by productId
    const stockByProduct = new Map<string, number>();
    inventoryData.forEach(item => {
      if (item.productId) {
        const current = stockByProduct.get(item.productId) || 0;
        stockByProduct.set(item.productId, current + (item.availableQuantity || 0));
      }
    });
    
    // Find products that are low stock AND not on order
    const lowStockItems: ProductLowStockAlert[] = [];
    
    for (const product of productsData) {
      // Skip if no minStockLevel set
      if (!product.minStockLevel || product.minStockLevel <= 0) continue;
      
      // Skip if already on order
      if (productsOnOrder.has(product.productId)) continue;
      
      const currentStock = stockByProduct.get(product.productId) || 0;
      
      // Check if below minimum
      if (currentStock < product.minStockLevel) {
        lowStockItems.push({
          productId: product.productId,
          productName: product.name,
          categoryId: product.categoryId,
          currentStock,
          minStockLevel: product.minStockLevel,
          suggestedOrderQty: product.minStockLevel - currentStock
        });
      }
    }
    
    // Sort by severity (lowest stock percentage first)
    lowStockItems.sort((a, b) => {
      const aRatio = a.currentStock / a.minStockLevel;
      const bRatio = b.currentStock / b.minStockLevel;
      return aRatio - bRatio;
    });
    
    dashboardLogger.info('Product low stock alerts calculated', {
      totalLowStock: lowStockItems.length,
      excludedOnOrder: productsOnOrder.size
    });
    
    timer();
    
    return {
      items: lowStockItems,
      totalCount: lowStockItems.length
    };
  }

  /**
   * Calculate body armor alerts from real DynamoDB inventory data
   */
  private calculateBodyArmorAlerts(inventoryData: any[], assignmentsData: any[]): BodyArmorAlerts {
    const timer = logger.startTimer('DashboardAPI', 'calculateBodyArmorAlerts');
    const now = new Date();
    
    const bodyArmorItems = inventoryData.filter(item => 
      item.bodyArmor && item.bodyArmor.expirationDate
    );

    dashboardLogger.debug('Analyzing body armor items from DynamoDB', {
      totalItems: inventoryData.length,
      bodyArmorCount: bodyArmorItems.length
    });

    const expired: BodyArmorAlert[] = [];
    const expiringSoon: BodyArmorAlert[] = [];

    for (const item of bodyArmorItems) {
      const expirationDate = new Date(item.bodyArmor.expirationDate);
      const daysUntilExpiration = Math.ceil((expirationDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

      const assignment = assignmentsData.find(a => 
        a.inventoryItemId === item.itemId && a.status === 'Active'
      );

      const alert: BodyArmorAlert = {
        itemId: item.itemId,
        itemName: item.name,
        serialNumber: item.serialNumber || 'N/A',
        expirationDate: item.bodyArmor.expirationDate,
        daysUntilExpiration: daysUntilExpiration,
        assignedToName: assignment ? assignment.assignedToUserName : undefined,
        status: item.status,
        ballisticLevel: item.bodyArmor.ballisticLevel || 'Unknown'
      };

      if (daysUntilExpiration < 0) {
        expired.push(alert);
      } else if (daysUntilExpiration <= 180) {
        expiringSoon.push(alert);
      }
    }

    expired.sort((a, b) => a.daysUntilExpiration - b.daysUntilExpiration);
    expiringSoon.sort((a, b) => a.daysUntilExpiration - b.daysUntilExpiration);

    const nextExpiration = expiringSoon.length > 0 ? expiringSoon[0] : null;

    dashboardLogger.info('Body armor analysis completed', {
      totalBodyArmor: bodyArmorItems.length,
      expired: expired.length,
      expiringSoon: expiringSoon.length,
      nextExpirationDays: nextExpiration?.daysUntilExpiration
    });

    timer();

    return {
      expired,
      expiringSoon,
      nextExpirationDate: nextExpiration?.expirationDate,
      nextExpirationDays: nextExpiration?.daysUntilExpiration,
      totalBodyArmor: bodyArmorItems.length
    };
  }

  /**
   * Calculate low stock alerts - simplified, no order status filtering
   */
  private calculateLowStockAlerts(inventoryData: any[]): LowStockAlerts {
    const timer = logger.startTimer('DashboardAPI', 'calculateLowStockAlerts');
    
    const bulkItems = inventoryData.filter(item => {
      const itemType = (item.itemType || item.trackingType || '').toUpperCase();
      const hasQuantity = item.totalQuantity !== undefined || 
                          item.quantity !== undefined || 
                          item.availableQuantity !== undefined;
      
      return itemType !== 'SERIALIZED' || hasQuantity;
    });

    dashboardLogger.debug('Analyzing bulk items from DynamoDB', {
      totalItems: inventoryData.length,
      bulkItemCount: bulkItems.length
    });

    const criticalStock: LowStockAlert[] = [];
    const lowStock: LowStockAlert[] = [];

    for (const item of bulkItems) {
      const availableQuantity = this.parseNumber(item.availableQuantity, 0);
      
      const minimumStockLevel = this.parseNumber(
        item.minStockLevel ?? 
        item.minimumStockLevel ?? 
        item.reorderLevel ?? 
        0
      );

      const totalQuantity = this.parseNumber(
        item.totalQuantity ?? 
        item.quantity ?? 
        availableQuantity
      );

      // Skip items without minimum stock levels set
      if (minimumStockLevel === 0) {
        continue;
      }

      const alert: LowStockAlert = {
        itemId: item.itemId,
        itemName: item.name,
        availableQuantity,
        minimumStockLevel,
        totalQuantity,
        category: item.category || 'Unknown'
      };

      if (availableQuantity === 0) {
        criticalStock.push(alert);
      } else if (availableQuantity <= minimumStockLevel) {
        lowStock.push(alert);
      }
    }

    // Sort by severity
    criticalStock.sort((a, b) => a.availableQuantity - b.availableQuantity);
    lowStock.sort((a, b) => (a.availableQuantity / a.minimumStockLevel) - (b.availableQuantity / b.minimumStockLevel));

    dashboardLogger.info('Low stock analysis completed', {
      totalBulkItems: bulkItems.length,
      criticalStock: criticalStock.length,
      lowStock: lowStock.length
    });

    timer();

    return {
      criticalStock,
      lowStock,
      totalBulkItems: bulkItems.length
    };
  }

  /**
   * Helper method to safely parse numbers with fallback
   */
  private parseNumber(value: any, defaultValue: number = 0): number {
    if (typeof value === 'number' && !isNaN(value)) {
      return value;
    }
    if (typeof value === 'string') {
      const parsed = parseInt(value, 10);
      return isNaN(parsed) ? defaultValue : parsed;
    }
    return defaultValue;
  }

  /**
   * Calculate onboarding alerts from real DynamoDB session data
   */
  private calculateOnboardingAlerts(onboardingData: any[]): OnboardingAlerts {
    const timer = logger.startTimer('DashboardAPI', 'calculateOnboardingAlerts');
    
    dashboardLogger.debug('Analyzing onboarding sessions from DynamoDB', {
      sessionCount: onboardingData.length
    });

    const now = new Date();
    const overdueSessions: OnboardingAlert[] = [];
    const recentSessions: OnboardingAlert[] = [];
    const readyToComplete: OnboardingAlert[] = [];

    for (const session of onboardingData) {
      if (session.status === 'Complete') {
        continue;
      }

      const createdDate = new Date(session.createdAt);
      const daysOld = Math.floor((now.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24));

      const alert: OnboardingAlert = {
        sessionId: session.sessionId,
        officerId: session.officerId,
        officerName: session.officerName || 'Unknown Officer',
        badgeNumber: session.badgeNumber || 'N/A',
        status: session.status,
        daysOld,
        reservedItems: (session.reservedItems || []).length,
        roleTemplateName: session.roleTemplateName || 'Unknown Role'
      };

      if (session.status === 'Ready' || (session.progress && session.progress.canComplete)) {
        readyToComplete.push(alert);
      } else if (daysOld > 14) {
        overdueSessions.push(alert);
      } else if (daysOld <= 7) {
        recentSessions.push(alert);
      }
    }

    overdueSessions.sort((a, b) => b.daysOld - a.daysOld);
    recentSessions.sort((a, b) => a.daysOld - b.daysOld);
    readyToComplete.sort((a, b) => b.daysOld - a.daysOld);

    const totalIncomplete = onboardingData.filter(s => s.status !== 'Complete').length;

    dashboardLogger.info('Onboarding analysis completed', {
      totalSessions: onboardingData.length,
      overdueSessions: overdueSessions.length,
      readyToComplete: readyToComplete.length,
      totalIncomplete
    });

    timer();

    return {
      overdueSessions,
      recentSessions,
      readyToComplete,
      totalIncomplete
    };
  }
}

export const dashboardAPI = new DashboardAPI();
